/*
  Product page buy box (v5), for sections/bjaf-pdp-main.liquid.

  - Gallery: swipe track with thumbnails and arrows.
  - Dog weight: swaps the dose line, the bundle lines and the prices a day. Every value is rendered by Liquid
    (data-* and <template> elements), so amounts keep Shopify's money format; nothing is formatted here.
  - Bundles: "Buy 1" or "Buy 2, get 1 free". Add to cart sends Kaching's line property (deal and bar from Kaching's
    app embed in <head>, script.kaching-bundles-deal-block-settings), exactly like the Kaching widget, so Kaching's
    discount applies. Without that data the jars are still added, just without the discount.
  - After adding, the cart drawer is re-rendered from the section HTML that comes back with the request and opened.
  - Sticky bar on phones once the main button has scrolled out of view.
  - Bundle builder (buy mode "Bundle", snippets/bjaf-pdp-bundle): "On its own" or "Bundle and save" with picked
    add-ons, the coat picker, the Clipper Oil row and the details popup. Add to cart adds the product and the picked
    add-ons, then applies the add-ons' discount codes merged with any code already in the cart. Totals are formatted
    from a Liquid-rendered sample amount, so they follow the visitor's currency format.
  - Simple buy mode (care, blades, accessories, the bed): option buttons pick the variant from the variant data the
    section renders (price already formatted by Liquid); price, button, sticky bar, gallery photo and ?variant= follow.
  - Reviews (bjaf-pdp-reviews v3): the product's Judge.me reviews from Judge.me's feed, star filters, "Read more",
    photo grid and photo viewer, desktop arrows.
*/
(() => {
  const root = () => window.Shopify?.routes?.root || '/';

  const kachingDeal = () => {
    const el = document.querySelector('script.kaching-bundles-deal-block-settings');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };

  const kachingProperty = (productId, units) => {
    const deal = kachingDeal();
    const bars = deal?.dealBars || [];
    const bar =
      units === 3
        ? bars.find((b) => Number(b.buyQuantity) === 2 && Number(b.getQuantity) === 1)
        : bars.find((b) => Number(b.quantity) === 1);
    if (!deal || !bar) return null;
    return JSON.stringify({
      deal: deal.nanoId,
      pp: Number(productId),
      main: true,
      v: deal.versionId,
      id: Math.random().toString(36).slice(2, 6),
      bid: bar.id,
    });
  };

  // Formats cents like Shopify's money filter, from a sample of 123456 rendered by Liquid (e.g. "1.234,56 kr")
  const moneyFormatter = (sample) => {
    const m = (sample || '').match(/^(.*?)1(\D?)234(?:(\D)(\d{2}))?(.*)$/);
    if (!m) return (cents) => (cents / 100).toFixed(2);
    const [, before, thousands, decimal, decimals, after] = m;
    return (cents) => {
      const value = decimals ? Math.round(cents) / 100 : Math.round(cents / 100);
      const [int, frac] = (decimals ? value.toFixed(2) : String(value)).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return before + grouped + (decimals ? decimal + frac : '') + after;
    };
  };

  const drawerSectionId = () => {
    const section = document.querySelector('cart-drawer')?.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  };

  class BjafPdp {
    constructor(el) {
      this.el = el;
      this.form = el.querySelector('[data-bp-form]');
      this.qty = el.querySelector('[data-bp-qty]');
      this.atc = el.querySelector('[data-bp-atc]');
      this.atcText = el.querySelector('[data-bp-atc-text]');
      this.save = el.querySelector('[data-bp-save]');
      this.sticky = el.querySelector('[data-bp-sticky]');
      this.weight = Number(el.querySelector('[data-bp-weight].is-on')?.dataset.bpWeight || 1);
      this.bundle = el.dataset.mode === 'bundle';
      this.simple = el.dataset.mode === 'simple';
      this.money = moneyFormatter(el.dataset.moneySample);
      this.initGallery();
      if (this.bundle) {
        this.initBundle();
      } else if (this.simple) {
        this.initSimple();
      } else {
        this.initWeight();
        this.initTiers();
      }
      this.initSubmit();
      this.initSticky();
    }

    refresh() {
      if (this.bundle) this.updateBundle();
      else if (this.simple) this.updateSimple();
      else this.updateTier();
    }

    /* ---------- Simple buy mode (options) ---------- */
    initSimple() {
      const data = this.el.querySelector('script[data-bp-variants]');
      this.variants = [];
      try {
        this.variants = data ? JSON.parse(data.textContent) : [];
      } catch (e) {
        this.variants = [];
      }
      this.opts = [...this.el.querySelectorAll('[data-bp-opt]')];
      this.opts.forEach((group) => {
        group.querySelectorAll('[data-bp-opt-value]').forEach((btn) => {
          btn.addEventListener('click', () => {
            group.querySelectorAll('[data-bp-opt-value]').forEach((b) => {
              const on = b === btn;
              b.classList.toggle('is-on', on);
              b.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
            const hint = group.querySelector('[data-bp-opt-hint]');
            if (hint) hint.textContent = btn.dataset.hint || '';
            this.pickVariant();
          });
        });
      });
      this.simplePrice = this.el.querySelector('.bp-price__now')?.textContent || '';
      this.updateSimple();
    }

    pickVariant() {
      const chosen = this.opts.map((group) => group.querySelector('[data-bp-opt-value].is-on')?.dataset.bpOptValue);
      const variant = this.variants.find((v) => v.options.every((value, i) => value === chosen[i]));
      if (!variant) return;
      this.el.dataset.variantId = variant.id;
      this.el.dataset.price = variant.cents;
      const idInput = this.form?.querySelector('input[name="id"]');
      if (idInput) idInput.value = variant.id;
      this.simplePrice = variant.price;
      this.atc.disabled = !variant.available;
      const now = this.el.querySelector('.bp-price__now');
      if (now) now.textContent = variant.price;
      if (variant.media) {
        const slide = this.el.querySelector(`[data-bp-slide][data-media-id="${variant.media}"]`);
        if (slide) slide.parentElement.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      this.updateSimple();
    }

    updateSimple() {
      this.resetAdded();
      if (this.atcText) {
        this.atcText.textContent = this.atc.disabled
          ? this.atc.dataset.soldout || ''
          : this.atc.dataset.label.replace('[amount]', this.simplePrice);
      }
      const name = this.el.querySelector('[data-bp-sticky-name]');
      const price = this.el.querySelector('[data-bp-sticky-price]');
      if (name) name.textContent = this.el.dataset.title;
      if (price) price.textContent = this.simplePrice;
    }

    /* ---------- Bundle builder (machines) ---------- */
    initBundle() {
      this.kit = this.el.querySelector('[data-bp-kit]');
      if (!this.kit) return;
      this.kitOn = false;
      this.kitBody = this.kit.querySelector('[data-bp-kit-body]');
      this.addons = [...this.kit.querySelectorAll('[data-bp-addon]')];
      this.oil = this.el.querySelector('[data-bp-oil]');
      this.pop = this.el.querySelector('[data-bp-pop]');

      this.kit.querySelectorAll('[data-bp-kit-choice]').forEach((radio) => {
        radio.addEventListener('change', () => this.setKit(radio.value === 'kit' && radio.checked));
      });
      this.addons.forEach((addon) => {
        addon.querySelector('[data-bp-addon-pick]').addEventListener('click', () => this.toggleAddon(addon));
        addon.querySelector('[data-bp-addon-more]')?.addEventListener('click', () => this.openPop(addon));
      });
      this.oil?.addEventListener('click', () => this.toggleAddon(this.oil));

      const coats = [...this.kit.querySelectorAll('[data-bp-coat]')];
      coats.forEach((coat) => coat.addEventListener('click', () => this.pickCoat(coat, coats)));
      const start = coats.find((c) => c.classList.contains('is-on')) || coats[0];
      if (start) this.pickCoat(start, coats);

      if (this.pop) {
        this.pop.querySelector('[data-bp-pop-close]')?.addEventListener('click', () => this.pop.close());
        this.pop.addEventListener('click', (event) => {
          if (event.target === this.pop) this.pop.close();
        });
        this.pop.querySelector('[data-bp-pop-btn]')?.addEventListener('click', () => {
          if (!this.popAddon) return;
          if (!this.kitOn) {
            const kitRadio = this.kit.querySelector('[data-bp-kit-choice][value="kit"]');
            kitRadio.checked = true;
            this.setKit(true);
          }
          this.toggleAddon(this.popAddon);
          this.pop.close();
        });
      }
      this.updateBundle();
    }

    setKit(on) {
      this.kitOn = on;
      if (this.kitBody) this.kitBody.hidden = !on;
      this.kit.querySelector('[data-bp-solo]')?.classList.toggle('is-on', !on);
      this.kit.querySelector('[data-bp-kitcard]')?.classList.toggle('is-on', on);
      this.updateBundle();
    }

    toggleAddon(addon) {
      const on = addon.dataset.on !== '1';
      addon.dataset.on = on ? '1' : '';
      addon.classList.toggle('is-on', on);
      const button = addon.matches('[data-bp-oil]') ? addon : addon.querySelector('[data-bp-addon-pick]');
      button.setAttribute('aria-checked', on ? 'true' : 'false');
      this.updateBundle();
    }

    pickCoat(coat, coats) {
      coats.forEach((c) => {
        const on = c === coat;
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      const recs = (coat.dataset.recs || '').split(',').filter(Boolean);
      this.addons.forEach((addon) => {
        const ribbon = addon.querySelector('[data-bp-addon-ribbon]');
        const r = recs.indexOf(addon.dataset.product);
        addon.classList.toggle('is-best', r === 0);
        addon.classList.toggle('is-good', r === 1);
        if (!ribbon) return;
        // Blades show "Best match" / "Also good"; cards show "For your coat"
        const blades = addon.closest('[data-layout]')?.dataset.layout === 'blades';
        if (blades) ribbon.textContent = r === 0 ? this.kit.dataset.ribbonBest : r === 1 ? this.kit.dataset.ribbonGood : '';
        else ribbon.textContent = r >= 0 ? this.kit.dataset.ribbonCoat || '' : '';
      });
      const rec = this.kit.querySelector('[data-bp-coat-rec]');
      if (rec) {
        rec.querySelector('[data-bp-coat-title]').textContent = coat.dataset.title || '';
        rec.querySelector('[data-bp-coat-text]').textContent = coat.dataset.text || '';
        rec.hidden = !coat.dataset.title && !coat.dataset.text;
      }
    }

    openPop(addon) {
      if (!this.pop) return;
      const tpl = addon.querySelector('template[data-bp-addon-pop]');
      const content = this.pop.querySelector('[data-bp-pop-content]');
      content.replaceChildren(tpl.content.cloneNode(true));
      const imgs = [...content.querySelectorAll('[data-bp-pop-img]')];
      const thumbs = [...content.querySelectorAll('[data-bp-pop-thumb]')];
      thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
        imgs.forEach((img) => { img.hidden = img.dataset.bpPopImg !== thumb.dataset.bpPopThumb; });
        thumbs.forEach((t) => t.classList.toggle('is-on', t === thumb));
      }));
      this.popAddon = addon;
      const btn = this.pop.querySelector('[data-bp-pop-btn]');
      const on = this.kitOn && addon.dataset.on === '1';
      const now = this.money(Number(addon.dataset.price) - Number(addon.dataset.off));
      btn.textContent = (on ? btn.dataset.remove : btn.dataset.add).replace('[amount]', now);
      btn.classList.toggle('is-on', on);
      this.pop.showModal();
    }

    pickedAddons() {
      const picked = this.kitOn ? this.addons.filter((a) => a.dataset.on === '1') : [];
      if (this.oil?.dataset.on === '1') picked.push(this.oil);
      return picked;
    }

    updateBundle() {
      if (!this.kit) return;
      const main = Number(this.el.dataset.price);
      const picked = this.pickedAddons();
      const full = picked.reduce((sum, a) => sum + Number(a.dataset.price), main);
      const total = picked.reduce((sum, a) => sum + Number(a.dataset.price) - Number(a.dataset.off), main);
      const saved = full - total;
      this.bundleTotal = total;

      const kitAddons = picked.filter((a) => a !== this.oil);
      const label = kitAddons.length && saved > 0 && this.atc.dataset.labelBundle ? this.atc.dataset.labelBundle : this.atc.dataset.label;
      if (this.atcText && !this.atc.disabled) this.atcText.textContent = label.replace('[amount]', this.money(total));
      this.resetAdded();

      const count = this.kit.querySelector('[data-bp-kit-count]');
      if (count) {
        count.hidden = !this.kitOn || kitAddons.length === 0;
        count.querySelector('[data-bp-kit-n]').textContent = kitAddons.length;
        const word = count.querySelector('[data-bp-kit-word]');
        word.textContent = kitAddons.length === 1 ? word.dataset.one : word.dataset.other;
      }
      const save = this.el.querySelector('[data-bp-kit-save]');
      if (save) {
        save.hidden = saved <= 0;
        save.querySelector('[data-bp-kit-full]').textContent = this.money(full);
        save.querySelector('[data-bp-kit-total]').textContent = this.money(total);
        const savedEl = save.querySelector('[data-bp-kit-saved]');
        savedEl.textContent = savedEl.dataset.template.replace('[amount]', this.money(saved));
      }
      const name = this.el.querySelector('[data-bp-sticky-name]');
      const price = this.el.querySelector('[data-bp-sticky-price]');
      if (name) name.textContent = this.el.dataset.title;
      if (price) price.textContent = this.money(total);
    }

    /* ---------- Gallery ---------- */
    initGallery() {
      const track = this.el.querySelector('[data-bp-track]');
      if (!track) return;
      const slides = [...track.querySelectorAll('[data-bp-slide]')];
      const thumbs = [...this.el.querySelectorAll('[data-bp-thumb]')];
      let current = 0;
      const show = (i) => {
        const n = (i + slides.length) % slides.length;
        track.scrollTo({ left: slides[n].offsetLeft, behavior: 'smooth' });
      };
      const mark = (i) => {
        current = i;
        thumbs.forEach((t, k) => {
          t.classList.toggle('is-on', k === i);
          if (k === i) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
        const thumb = thumbs[i];
        if (thumb) thumb.parentElement.scrollTo({ left: thumb.offsetLeft - 16, behavior: 'smooth' });
      };
      thumbs.forEach((t) => t.addEventListener('click', () => show(Number(t.dataset.bpThumb))));
      this.el.querySelector('[data-bp-prev]')?.addEventListener('click', () => show(current - 1));
      this.el.querySelector('[data-bp-next]')?.addEventListener('click', () => show(current + 1));
      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          if (i !== current && slides[i]) mark(i);
          ticking = false;
        });
      }, { passive: true });
    }

    /* ---------- Dog weight ---------- */
    initWeight() {
      this.el.querySelectorAll('[data-bp-weight]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.weight = Number(btn.dataset.bpWeight);
          this.el.querySelectorAll('[data-bp-weight]').forEach((b) => {
            const on = b === btn;
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          this.updateWeight();
        });
      });
    }

    updateWeight() {
      const w = this.weight;
      const dose = this.el.querySelector('[data-bp-dose]');
      if (dose && dose.hasAttribute(`data-dose-${w}`)) dose.textContent = dose.getAttribute(`data-dose-${w}`);
      [1, 2].forEach((t) => {
        const tpl = this.el.querySelector(`template[data-tier="${t}"][data-w="${w}"]`);
        if (!tpl) return;
        const sub = this.el.querySelector(`[data-bp-sub="${t}"]`);
        const day = this.el.querySelector(`[data-bp-day="${t}"]`);
        if (sub) sub.textContent = tpl.dataset.sub;
        if (day) day.textContent = tpl.dataset.day;
        if (t === 2) {
          const from = this.el.querySelector('[data-bp-from]');
          if (from) from.textContent = tpl.dataset.from;
        }
      });
    }

    /* ---------- Bundles ---------- */
    initTiers() {
      this.el.querySelectorAll('[data-bp-tier]').forEach((radio) => {
        radio.addEventListener('change', () => this.updateTier());
      });
      this.updateTier();
    }

    selectedTier() {
      return this.el.querySelector('[data-bp-tier]:checked') || this.el.querySelector('[data-bp-tier]');
    }

    updateTier() {
      const tier = this.selectedTier();
      if (!tier) return;
      const units = Number(tier.dataset.units);
      if (this.qty) this.qty.value = units;
      this.resetAdded();
      if (this.atcText && !this.atc.disabled) this.atcText.textContent = this.atc.dataset.label.replace('[amount]', tier.dataset.total);
      if (this.save) this.save.hidden = units < 2;
      const name = this.el.querySelector('[data-bp-sticky-name]');
      const price = this.el.querySelector('[data-bp-sticky-price]');
      if (name) name.textContent = tier.dataset.label;
      if (price) {
        price.textContent = units > 1 ? `${tier.dataset.total} · ${price.dataset.units.replace('[count]', units)}` : tier.dataset.total;
      }
    }

    resetAdded() {
      this.atc?.classList.remove('is-added');
    }

    /* ---------- Add to cart ---------- */
    initSubmit() {
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.add();
      });
    }

    async add() {
      if (!this.atc || this.atc.disabled) return;
      const items = [];
      let codes = [];
      if (this.simple) {
        items.push({ id: Number(this.el.dataset.variantId), quantity: 1 });
      } else if (this.bundle) {
        items.push({ id: Number(this.el.dataset.variantId), quantity: 1 });
        this.pickedAddons().forEach((a) => {
          items.push({ id: Number(a.dataset.variant), quantity: 1 });
          if (a.dataset.code) codes.push(a.dataset.code);
        });
        codes = [...new Set(codes)];
      } else {
        const units = Number(this.selectedTier()?.dataset.units || 1);
        const item = { id: Number(this.el.dataset.variantId), quantity: units };
        const property = kachingProperty(this.el.dataset.productId, units);
        if (property) item.properties = { __kaching_bundles: property };
        items.push(item);
      }

      const sectionId = drawerSectionId();
      const sections = sectionId ? { sections: [sectionId], sections_url: window.location.pathname } : {};
      const body = { items, ...(codes.length ? {} : sections) };

      this.atc.classList.add('is-busy');
      try {
        const response = await fetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Error');
        let cart = await fetch(root() + 'cart.js').then((r) => r.json());
        if (codes.length) {
          // Keep the codes already in the cart (e.g. the cart drawer's secret offer) and add the bundle codes
          const current = (cart.discount_codes || []).map((d) => d.code);
          const merged = [...current];
          codes.forEach((code) => {
            if (!merged.some((c) => c.toLowerCase() === code.toLowerCase())) merged.push(code);
          });
          cart = await fetch(root() + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: merged.join(','), ...sections }),
          }).then((r) => r.json());
        } else {
          cart.sections = data.sections;
        }
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
        );
        this.atc.classList.add('is-added');
        if (this.atcText) this.atcText.textContent = this.atc.dataset.added;
        const drawer = document.querySelector('cart-drawer');
        if (drawer?.show) drawer.show();
        window.setTimeout(() => this.refresh(), 2500);
      } catch (error) {
        if (this.atcText) this.atcText.textContent = error.message;
        window.setTimeout(() => this.refresh(), 3000);
      } finally {
        this.atc.classList.remove('is-busy');
      }
    }

    /* ---------- Sticky bar (phones) ---------- */
    initSticky() {
      if (!this.sticky || !this.atc || !('IntersectionObserver' in window)) return;
      const btn = this.sticky.querySelector('[data-bp-sticky-btn]');
      btn?.addEventListener('click', () => this.add());
      const observer = new IntersectionObserver(([entry]) => {
        // Show once the main button is above the viewport (scrolled past), hide when it is visible again
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sticky.classList.toggle('is-on', past);
        this.sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
        if (btn) btn.tabIndex = past ? 0 : -1;
      });
      observer.observe(this.atc);
    }
  }

  /* ---------- Reviews (sections/bjaf-pdp-reviews.liquid, v3) ----------
     The Judge.me metafield behind the first cards only holds Judge.me's first page of reviews. This loads the
     product's reviews from Judge.me's public widget endpoint (the one Judge.me's own widget uses): the first page of
     reviews in the shop's language, every page of the other languages (up to MAX_PAGES), and for a star level the
     first pages don't cover, Judge.me's filter_rating. Cards keep the rules of bjaf-reviews-collect (the visitor's
     language or Judge.me's translation, then English; no untranslated Scandinavian text; no old product-name-only
     reviews) and are sorted with photos first, then the longer reviews. Texts are set with textContent, never as HTML.
     The star bars filter the cards; the status line switches between the default view (4 and 5 stars) and all
     reviews. Photos: 8 tiles, the 8th shows "+N", every photo opens in the viewer (<dialog>). */
  const REVIEW_LIMIT = 40;
  const MAX_PAGES = 5;
  // Judge.me sometimes tags Scandinavian reviews as English; untranslated "English" texts with these are skipped
  const FOREIGN_MARKS = ['æ', 'ø', 'å', 'ä', 'ö', ' og ', ' jeg ', ' det ', ' er ', ' ikke ', ' meget ', ' til ', ' med ', ' hun ', ' han ', ' som ', ' har ', ' vores ', ' min ', ' mit ', ' mycket ', ' och ', ' bra ', ' kommer ', 'hurtig', ' firma ', ' godt ', ' god ', ' virker '];

  const plainText = (html) => new DOMParser().parseFromString(html || '', 'text/html').body.textContent.replace(/\s+/g, ' ').trim();
  const reviewsOf = (data) => [...(data.primary_language_reviews || []), ...(data.other_language_reviews || []), ...(data.photo_gallery || [])];

  const formatDates = (scope) => {
    let fmt;
    try {
      fmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
      return;
    }
    scope.querySelectorAll('time.bp-rcard__date[datetime]').forEach((el) => {
      const d = new Date(String(el.getAttribute('datetime')).replace(' UTC', 'Z').replace(' ', 'T'));
      if (!Number.isNaN(d.getTime())) el.textContent = fmt.format(d);
    });
  };

  // "Read more" only on cards whose text is cut off
  const initMore = (scope, more, less) => {
    requestAnimationFrame(() => {
      scope.querySelectorAll('.bp-rcard').forEach((card) => {
        const text = card.querySelector('.bp-rcard__text');
        const btn = card.querySelector('[data-bp-rev-more]');
        if (!text || !btn || btn.dataset.ready) return;
        btn.dataset.ready = '1';
        if (text.scrollHeight <= text.clientHeight + 2) return;
        btn.hidden = false;
        btn.textContent = more;
        btn.addEventListener('click', () => {
          const open = card.classList.toggle('is-open');
          btn.textContent = open ? less : more;
        });
      });
    });
  };

  class BjafReviews {
    constructor(row) {
      this.row = row;
      this.section = row.closest('[data-bp-rv]');
      this.tpl = this.section.querySelector('template[data-bp-rev-tpl]');
      this.d = this.section.dataset;
      this.filter = 'top';
      this.items = new Map();
      this.loadedStars = new Set();
      this.photos = [];
      this.initNav();
      this.initControls();
      this.initPhotos();
      formatDates(this.section);
      initMore(this.row, this.d.more, this.d.less);
      this.load();
    }

    initNav() {
      const step = (dir) => this.row.scrollBy({ left: dir * this.row.clientWidth * 0.9, behavior: 'smooth' });
      this.section.querySelector('[data-bp-rev-prev]')?.addEventListener('click', () => step(-1));
      this.section.querySelector('[data-bp-rev-next]')?.addEventListener('click', () => step(1));
    }

    initControls() {
      this.bars = [...this.section.querySelectorAll('[data-bp-rev-star]')];
      this.bars.forEach((bar) => bar.addEventListener('click', () => {
        const star = bar.dataset.bpRevStar;
        this.show(this.filter === star ? 'top' : star);
      }));
      this.switchBtn = this.section.querySelector('[data-bp-rev-switch]');
      this.switchBtn?.addEventListener('click', () => this.show(this.filter === 'top' ? 'all' : 'top'));
    }

    async fetchJson(extra) {
      const d = this.row.dataset;
      const url = `https://judge.me/reviews/reviews_for_widget?url=${encodeURIComponent(d.shop)}&shop_domain=${encodeURIComponent(d.shop)}&platform=shopify&per_page=60&product_id=${encodeURIComponent(d.productId)}${extra}`;
      try {
        const res = await fetch(url);
        return res.ok ? await res.json() : null;
      } catch (e) {
        return null;
      }
    }

    // Loads page 1 plus the other-language pages of one query ('' = every rating, '&filter_rating=N' = one star level)
    async fetchAll(query) {
      const first = await this.fetchJson(`&page=1${query}`);
      if (!first) return false;
      this.add(first);
      const pages = Math.min(first.other_language_pagination?.total_pages || 1, MAX_PAGES);
      const rest = [];
      for (let p = 2; p <= pages; p += 1) rest.push(this.fetchJson(`&page=1&other_language_page=${p}${query}`));
      (await Promise.all(rest)).forEach((data) => data && this.add(data));
      return true;
    }

    async load() {
      if (!this.tpl || !this.row.dataset.shop || !this.row.dataset.productId) return;
      if (!(await this.fetchAll(''))) return;
      this.renderPhotos();
      this.show(this.filter);
      this.reveal();
    }

    // Adds the reviews of one feed response, with the same text rules as bjaf-reviews-collect
    add(data) {
      const d = this.row.dataset;
      const locale = d.locale;
      const translations = data.review_translations || {};
      reviewsOf(data).forEach((r) => {
        if (!r.uuid) return;
        const known = this.items.get(r.uuid);
        if (known) {
          // photo_gallery entries carry fewer fields; keep the fuller copy
          if (!known.r.location_country && r.location_country) known.r = { ...known.r, ...r };
          return;
        }
        const tr = translations[r.uuid] || {};
        const original = plainText(r.body_html || r.body);
        let text = '';
        let title = '';
        let translated = false;
        if (r.language === locale) {
          text = original;
          title = r.title;
        } else if (tr[locale]?.body) {
          ({ body: text, title } = tr[locale]);
          translated = true;
        } else if (r.language === 'en') {
          text = original;
          title = r.title;
        } else if (tr.en?.body) {
          ({ body: text, title } = tr.en);
          translated = true;
        }
        text = plainText(text);
        title = plainText(title);
        if (!text) return;
        if (!translated && r.language === 'en') {
          const probe = ` ${text.toLowerCase()} `;
          if (FOREIGN_MARKS.some((m) => probe.includes(m))) return;
        }
        // Old imports whose text is only the old store's product name (or that name plus a pre-order note)
        const name = String(r.product_title || '').toLowerCase().trim();
        const lower = text.toLowerCase();
        if (name && (lower === name || (lower.startsWith(name) && text.length < name.length + 50))) return;
        this.items.set(r.uuid, {
          r, text, title, translated,
          rating: Math.round(Number(r.rating)),
          own: r.language === locale || !!tr[locale]?.body,
          pics: (r.pictures_urls || []).filter((p) => p && (p.huge || p.original)),
        });
      });
    }

    list(filter) {
      const minRating = Number(this.row.dataset.minRating) || 4;
      const minLength = Number(this.row.dataset.minLength) || 1;
      return [...this.items.values()]
        .filter((it) => (filter === 'top' ? it.rating >= minRating && it.text.length >= minLength : filter === 'all' ? true : it.rating === Number(filter)))
        // The visitor's language first, then reviews with photos, then the longer ones
        .sort((a, b) => (b.own - a.own) || ((b.pics.length > 0) - (a.pics.length > 0)) || (b.text.length - a.text.length))
        .slice(0, REVIEW_LIMIT);
    }

    async show(filter) {
      this.filter = filter;
      this.updateControls();
      let items = this.list(filter);
      const bar = this.bars.find((b) => b.dataset.bpRevStar === filter);
      // A star level the first pages don't cover (reviews are sorted highest first): ask Judge.me for that level
      if (bar && items.length < Number(bar.dataset.count || 0) && !this.loadedStars.has(filter)) {
        this.loadedStars.add(filter);
        await this.fetchAll(`&filter_rating=${encodeURIComponent(filter)}`);
        if (this.filter !== filter) return;
        this.renderPhotos();
        items = this.list(filter);
      }
      this.row.textContent = '';
      items.forEach((it) => this.row.appendChild(this.card(it)));
      this.row.scrollLeft = 0;
      this.section.querySelector('[data-bp-rev-empty]').hidden = items.length > 0;
      initMore(this.row, this.d.more, this.d.less);
    }

    updateControls() {
      const f = this.filter;
      const status = this.section.querySelector('[data-bp-rev-status]');
      if (status) status.textContent = f === 'top' ? this.d.statusTop : f === 'all' ? this.d.statusAll : this.d.statusStar.replace('[n]', f);
      if (this.switchBtn) this.switchBtn.textContent = f === 'top' ? this.d.switchAll : this.d.switchTop;
      this.bars.forEach((b) => {
        const on = b.dataset.bpRevStar === f;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    // A product whose first reviews were all filtered out starts hidden behind the shop reviews; show it once it has cards
    reveal() {
      if (!this.section.hidden || !this.row.children.length) return;
      this.section.hidden = false;
      this.section.parentElement.querySelector('[data-bp-rev-fallback]')?.setAttribute('hidden', '');
    }

    displayName(r) {
      const name = r.reviewer_name;
      return r.is_anonymous_reviewer || !name || name === 'Anonymous' || name === 'Customer' ? this.row.dataset.anonymous : name;
    }

    card(it) {
      const { r, text, title, translated, rating, pics } = it;
      const d = this.row.dataset;
      const node = this.tpl.content.firstElementChild.cloneNode(true);
      const name = this.displayName(r);
      node.dataset.uuid = r.uuid;
      node.querySelector('.bp-rcard__avatar').textContent = name.charAt(0);
      node.querySelector('.bp-rcard__name').textContent = name;
      node.querySelector('.bp-rcard__ver').hidden = !r.verified_buyer;

      const code = String(r.country_code_show_flag || r.location_country_code || '').toLowerCase();
      const country = r.location_country || String(r.location || '').replace(/[()]/g, '').trim();
      const from = node.querySelector('.bp-rcard__from');
      from.hidden = !country;
      from.querySelector('span').textContent = country;
      const flag = from.querySelector('img');
      flag.hidden = !code;
      if (code) flag.src = `https://cdn.shopify.com/static/images/flags/${code}.svg`;

      const date = node.querySelector('.bp-rcard__date');
      date.setAttribute('datetime', r.created_at || '');
      date.textContent = '';

      const stars = node.querySelector('.bp-rcard__stars');
      stars.setAttribute('aria-label', d.ratingLabel.replace('[r]', rating));
      stars.querySelectorAll('svg').forEach((svg, i) => svg.classList.toggle('is-on', i < rating));
      const titleEl = node.querySelector('.bp-rcard__title');
      titleEl.textContent = title;
      titleEl.hidden = !title || title.toLowerCase() === text.toLowerCase();
      node.querySelector('.bp-rcard__text').textContent = text;

      const picsEl = node.querySelector('.bp-rcard__pics');
      picsEl.textContent = '';
      picsEl.hidden = !pics.length;
      pics.slice(0, 3).forEach((p, i) => picsEl.appendChild(this.tile(r.uuid, i, p, name)));
      node.querySelector('.bp-rcard__foot').hidden = !translated;
      formatDates(node);
      return node;
    }

    tile(uuid, index, pic, label) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bp-rv-tile';
      btn.dataset.bpRevPhoto = uuid;
      btn.dataset.index = String(index);
      btn.setAttribute('aria-label', label);
      const img = document.createElement('img');
      img.src = pic.compact || pic.small || pic.huge;
      img.alt = '';
      img.width = 160;
      img.height = 160;
      img.loading = 'lazy';
      btn.appendChild(img);
      return btn;
    }

    // ---------- Photos and the viewer ----------
    initPhotos() {
      this.wrap = this.section.querySelector('[data-bp-rev-photos]');
      this.grid = this.section.querySelector('[data-bp-rev-grid]');
      this.lb = this.section.querySelector('[data-bp-rev-lb]');
      this.section.addEventListener('click', (event) => {
        const tile = event.target.closest('[data-bp-rev-photo]');
        if (!tile || !this.section.contains(tile)) return;
        this.openPhoto(tile.dataset.bpRevPhoto, Number(tile.dataset.index) || 0, tile.dataset.at);
      });
      if (!this.lb) return;
      this.lb.querySelector('[data-bp-lb-close]')?.addEventListener('click', () => this.lb.close());
      this.lb.querySelector('[data-bp-lb-prev]')?.addEventListener('click', () => this.step(-1));
      this.lb.querySelector('[data-bp-lb-next]')?.addEventListener('click', () => this.step(1));
      // A click on the dimmed backdrop (outside the box) closes the viewer
      this.lb.addEventListener('click', (event) => {
        if (event.target === this.lb) this.lb.close();
      });
      this.lb.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      });
    }

    renderPhotos() {
      if (!this.grid) return;
      const all = [...this.items.values()].filter((it) => it.pics.length)
        .sort((a, b) => (b.rating - a.rating) || (b.own - a.own));
      this.photos = all.flatMap((it) => it.pics.map((pic, i) => ({ it, pic, i })));
      if (!this.photos.length) return;
      const max = Number(this.d.maxTiles) || 8;
      this.grid.textContent = '';
      this.photos.slice(0, max).forEach((p, k) => {
        const btn = this.tile(p.it.r.uuid, p.i, p.pic, this.displayName(p.it.r));
        btn.dataset.at = String(k);
        if (k === max - 1 && this.photos.length > max) {
          const more = document.createElement('span');
          more.className = 'bp-rv-tile__more';
          more.setAttribute('aria-hidden', 'true');
          more.textContent = `+${this.photos.length - (max - 1)}`;
          btn.appendChild(more);
        }
        this.grid.appendChild(btn);
      });
      this.wrap.hidden = false;
    }

    openPhoto(uuid, index, at) {
      if (!this.lb || !this.photos.length) return;
      let k = at !== undefined ? Number(at) : this.photos.findIndex((p) => p.it.r.uuid === uuid && p.i === index);
      if (k < 0) k = this.photos.findIndex((p) => p.it.r.uuid === uuid);
      if (k < 0) return;
      this.at = k;
      this.paint();
      if (!this.lb.open) this.lb.showModal();
    }

    step(dir) {
      if (!this.photos.length) return;
      this.at = (this.at + dir + this.photos.length) % this.photos.length;
      this.paint();
    }

    paint() {
      const { it, pic } = this.photos[this.at];
      const name = this.displayName(it.r);
      const img = this.lb.querySelector('[data-bp-lb-img]');
      img.src = pic.huge || pic.original;
      img.alt = name;
      const stars = this.lb.querySelector('[data-bp-lb-stars]');
      stars.setAttribute('aria-label', this.row.dataset.ratingLabel.replace('[r]', it.rating));
      stars.querySelectorAll('svg').forEach((svg, i) => svg.classList.toggle('is-on', i < it.rating));
      this.lb.querySelector('[data-bp-lb-text]').textContent = it.text;
      this.lb.querySelector('[data-bp-lb-name]').textContent = name;
      this.lb.querySelector('[data-bp-lb-pos]').textContent = `${this.at + 1} / ${this.photos.length}`;
    }
  }

  const init = () => {
    document.querySelectorAll('[data-bp]').forEach((el) => {
      if (!el.bjafPdp) el.bjafPdp = new BjafPdp(el);
    });
    document.querySelectorAll('[data-bp-reviews]').forEach((el) => {
      if (!el.bjafReviews) el.bjafReviews = new BjafReviews(el);
    });
    // Shop reviews shown when the product has none: desktop arrows, dates and "Read more"
    document.querySelectorAll('[data-bp-reviews-static]').forEach((row) => {
      if (row.bjafNav) return;
      row.bjafNav = true;
      const section = row.closest('.bp-sec__inner');
      const step = (dir) => row.scrollBy({ left: dir * row.clientWidth * 0.9, behavior: 'smooth' });
      section?.querySelector('[data-bp-rev-prev]')?.addEventListener('click', () => step(-1));
      section?.querySelector('[data-bp-rev-next]')?.addEventListener('click', () => step(1));
      formatDates(row);
      initMore(row, row.dataset.more, row.dataset.less);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);

  // Recently viewed products for the checkout upsell ("You looked at this earlier"): the
  // handles, newest first, go into the cart attribute _bjaf_viewed (read by the Bjaf Checkout
  // app; the upsell skips products already in the cart).
  const rememberViewed = () => {
    const handle = decodeURIComponent(location.pathname.match(/\/products\/([^/?#]+)/)?.[1] || '');
    if (!handle) return;
    let list = [];
    try {
      list = JSON.parse(localStorage.getItem('bjaf_viewed') || '[]');
    } catch (e) {}
    list = [handle, ...list.filter((h) => h !== handle)].slice(0, 6);
    try {
      localStorage.setItem('bjaf_viewed', JSON.stringify(list));
    } catch (e) {}
    fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _bjaf_viewed: list.join(',') } }),
    }).catch(() => {});
  };
  rememberViewed();
})();
